import { Box, Container, Paper, Typography } from "@mui/material";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";

const estimates = [
  {
    title: "Product & Development Cost",
    desc: "Estimate complete product costs including UI/UX, frontend, backend, APIs, integrations, testing, and maintenance.",
    icon: <TrendingUpRoundedIcon />,
  },
  {
    title: "Website & App Pricing",
    desc: "Get clear cost estimates for websites and mobile apps based on features, tech stack, performance, and scalability.",
    icon: <LanguageRoundedIcon />,
  },
  {
    title: "SEO & Marketing Budget",
    desc: "Calculate SEO, content marketing, and paid campaign budgets using competition, keywords, and growth goals.",
    icon: <CampaignRoundedIcon />,
  },
  {
    title: "Time & Resource Planning",
    desc: "Estimate timelines, team size, and effort required to deliver projects efficiently without cost overruns.",
    icon: <ScheduleRoundedIcon />,
  },
  {
    title: "Revenue & ROI Forecast",
    desc: "Understand potential revenue, conversion impact, and ROI before making critical investment decisions.",
    icon: <ShowChartRoundedIcon />,
  },
  {
    title: "Scaling & Growth Cost",
    desc: "Plan future costs for traffic growth, infrastructure scaling, feature expansion, and global reach.",
    icon: <PublicRoundedIcon />,
  },
];

export default function WhatWeEstimate() {
  return (
    <Box
      sx={{
        py: { xs: 4, md: 4 },
        backgroundColor: "var(--color-arctic)",
      }}
    >
      <Container maxWidth="lg">
        <Box textAlign="center" mb={{ xs: 4, md: 6 }}>
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "var(--color-midnight-60)",
              mb: 0.8,
            }}
          >
            WHAT WE ESTIMATE
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "1.5rem", md: "2rem" },
              fontWeight: 800,
              color: "var(--color-midnight)",
              letterSpacing: "-0.015em",
            }}
          >
            Clear costs.{" "}
            <Box component="span" sx={{ color: "var(--color-fiery)" }}>
              Confident planning.
            </Box>
          </Typography>

          <Typography
            sx={{
              mt: 1.2,
              maxWidth: 640,
              mx: "auto",
              fontSize: { xs: "0.9rem", md: "1rem" },
              lineHeight: 1.65,
              color: "var(--color-midnight-70)",
            }}
          >
            Smart cost insights that help founders and teams plan, invest, and
            scale with confidence.
          </Typography>
        </Box>
        {/* ===== Cards ===== */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 2.5, md: 3 },
            mt: { xs: 4, md: 6 },
          }}
        >
          {estimates.map((item, index) => (
            <Paper
              elevation={0}
              sx={{
                position: "relative",
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 46%",
                  md: "1 1 22%",
                },
                minHeight: { xs: 210, md: 240 },
                p: { xs: 3, md: 4 },
                borderRadius: 2,

                /* Premium surface */
                background:
                  "linear-gradient(180deg, #FFFFFF 0%, rgba(246,247,235,0.85) 100%)",

                border: "1px solid var(--color-midnight-15)",

                /* Brand accent spine */
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "inherit",
                  pointerEvents: "none",
                  boxShadow: "inset 3px 0 0 rgba(233,79,55,0.15)",
                },

                transition:
                  "transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease",

                "&:hover": {
                  transform: "translateY(-6px)",
                  borderColor: "rgba(233,79,55,0.45)",
                  boxShadow:
                    "0 22px 48px rgba(15,58,66,0.14), inset 3px 0 0 rgba(233,79,55,0.45)",
                },
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,

                  background:
                    "linear-gradient(180deg, rgba(233,79,55,0.15), rgba(233,79,55,0.05))",
                  color: "var(--color-fiery)",
                }}
              >
                {item.icon}
              </Box>

              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  color: "var(--color-midnight)",
                  mb: 1.3,
                }}
              >
                {item.title}
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.92rem",
                  lineHeight: 1.7,
                  color: "var(--color-midnight-70)",
                }}
              >
                {item.desc}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
